/**
 * Cluster Detail — read-first view of a single unserved church cluster.
 *
 * Renders metadata, LLM reasoning, an embedded Google Maps preview, the raw
 * sources that contributed to the cluster, and the history of prior human
 * corrections. The "Correct this" button reveals an inline CorrectionForm.
 *
 * Data shape from GET /api/admin/dashboard/analytics/church-demand/clusters/:id:
 *   { cluster: {...}, sources: [...], corrections: [...] }
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { churchDemandAPI } from '../../../services/api';
import ClusterLocationEmbed from './components/ClusterLocationEmbed';
import CorrectionForm from './components/CorrectionForm';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const TRUST_BADGE_CLASS = {
  auto_high: 'bg-green-100 text-green-800',
  auto_medium: 'bg-yellow-100 text-yellow-800',
  auto_low: 'bg-orange-100 text-orange-800',
  human_confirmed: 'bg-blue-100 text-blue-800',
  human_overridden: 'bg-purple-100 text-purple-800',
};

const STATUS_BADGE_CLASS = {
  unreviewed: 'bg-gray-100 text-gray-700',
  reviewed_ok: 'bg-green-50 text-green-700',
  corrected: 'bg-purple-50 text-purple-700',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatNumber(n) {
  if (n == null) return '—';
  if (typeof n === 'number') return n.toLocaleString();
  return String(n);
}

function MetadataRow({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{children}</dd>
    </div>
  );
}

// Parse a Google formatted_address into city/state for US addresses.
// Returns { city: null, state: null } on any parse failure (foreign address,
// malformed string, etc.) — caller should treat nulls as "couldn't parse"
// and leave the corresponding form fields blank for the operator to fill.
function parseUsAddress(formatted) {
  if (!formatted || typeof formatted !== 'string') return { city: null, state: null };
  const parts = formatted.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length && parts[parts.length - 1] === 'USA') parts.pop();
  if (parts.length < 2) return { city: null, state: null };
  const last = parts[parts.length - 1];
  const stateMatch = last.match(/^([A-Z]{2})\b/);
  if (!stateMatch) return { city: null, state: null };
  return { city: parts[parts.length - 2], state: stateMatch[1] };
}

// Build the CorrectionForm prefill from a Places candidate. Fields with
// no candidate data are left out so the form falls back to cluster defaults.
function placesCandidateToPrefill(candidate) {
  if (!candidate) return null;
  const { city, state } = parseUsAddress(candidate.formatted_address);
  const prefill = {};
  if (candidate.name) prefill.canonical_name = candidate.name;
  if (candidate.formatted_address) prefill.address = candidate.formatted_address;
  if (city) prefill.city = city;
  if (state) prefill.state = state;
  if (candidate.phone) prefill.phone = candidate.phone;
  if (candidate.website) prefill.website = candidate.website;
  return prefill;
}

function describeBeforeAfter(before, after) {
  if (!before && !after) return null;
  const changed = [];
  const keys = new Set([
    ...Object.keys(before || {}),
    ...Object.keys(after || {}),
  ]);
  for (const key of keys) {
    const b = before?.[key];
    const a = after?.[key];
    if (JSON.stringify(b) !== JSON.stringify(a)) {
      changed.push({ key, before: b, after: a });
    }
  }
  return changed;
}

export default function ClusterDetail() {
  const { clusterId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [correctionPrefill, setCorrectionPrefill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await churchDemandAPI.cluster(clusterId);
      setData(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setNotFound(true);
      } else {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Failed to load cluster detail'
        );
      }
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const handleCorrectionSuccess = () => {
    setShowCorrectionForm(false);
    setCorrectionPrefill(null);
    load();
  };

  const handleCorrectionCancel = () => {
    setShowCorrectionForm(false);
    setCorrectionPrefill(null);
  };

  const handleUsePlacesMatch = (candidate) => {
    setCorrectionPrefill(placesCandidateToPrefill(candidate));
    setShowCorrectionForm(true);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 text-center text-gray-500">
        Loading cluster…
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <Link
          to="/church-demand/clusters"
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to cluster list
        </Link>
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-1">
            Cluster not found
          </h2>
          <p className="text-sm text-gray-500">
            This cluster may have been rejected, merged into another cluster, or
            deleted. It is not visible in the current view.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link
          to="/church-demand/clusters"
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to cluster list
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm">
          <strong className="font-semibold">Failed to load:</strong> {error}
        </div>
      </div>
    );
  }

  if (!data || !data.cluster) return null;

  const { cluster, places_candidate: placesCandidate = null, sources = [], corrections = [] } = data;
  const title = cluster.canonical_name || cluster.name_slug || 'Unknown cluster';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Link
            to="/church-demand/clusters"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to cluster list
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                TRUST_BADGE_CLASS[cluster.trust_level] ||
                'bg-gray-100 text-gray-700'
              }`}
            >
              {cluster.trust_level || 'unknown'}
            </span>
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                STATUS_BADGE_CLASS[cluster.human_status] ||
                'bg-gray-100 text-gray-700'
              }`}
            >
              {cluster.human_status || '—'}
            </span>
          </div>
        </div>
        {!showCorrectionForm && (
          <button
            type="button"
            onClick={() => setShowCorrectionForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 min-h-[44px]"
          >
            <PencilSquareIcon className="w-4 h-4" />
            Correct this
          </button>
        )}
      </div>

      {/* Correction form (inline) */}
      {showCorrectionForm && (
        <CorrectionForm
          cluster={cluster}
          prefillOverride={correctionPrefill}
          onSuccess={handleCorrectionSuccess}
          onCancel={handleCorrectionCancel}
        />
      )}

      {/* Metadata card */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
          Metadata
        </h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetadataRow label="Denomination">
            {cluster.denomination || '—'}
          </MetadataRow>
          <MetadataRow label="City / State">
            {[cluster.city, cluster.state].filter(Boolean).join(', ') || '—'}
          </MetadataRow>
          <MetadataRow label="Address">{cluster.address || '—'}</MetadataRow>
          <MetadataRow label="Website">
            {cluster.website ? (
              <a
                href={cluster.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline break-all"
              >
                {cluster.website}
              </a>
            ) : (
              '—'
            )}
          </MetadataRow>
          <MetadataRow label="Phone">{cluster.phone || '—'}</MetadataRow>
          <MetadataRow label="Places ID">
            <code className="text-xs break-all">{cluster.places_id || '—'}</code>
          </MetadataRow>
          <MetadataRow label="Places match score">
            {cluster.places_match_score != null
              ? cluster.places_match_score.toFixed(2)
              : '—'}
          </MetadataRow>
          <MetadataRow label="LLM confidence">
            {cluster.llm_confidence != null
              ? cluster.llm_confidence.toFixed(2)
              : '—'}
          </MetadataRow>
          <MetadataRow label="Coordinates">
            {cluster.lat != null && cluster.lng != null
              ? `${cluster.lat.toFixed(4)}, ${cluster.lng.toFixed(4)}`
              : '—'}
          </MetadataRow>
          <MetadataRow label="Coord source">
            {cluster.coord_source || '—'}
          </MetadataRow>
          <MetadataRow label="Signal count">
            {formatNumber(cluster.signal_count)}
          </MetadataRow>
          <MetadataRow label="Unique users">
            {formatNumber(cluster.unique_user_count)}
          </MetadataRow>
          <MetadataRow label="First seen">
            {formatDateTime(cluster.first_seen_at)}
          </MetadataRow>
          <MetadataRow label="Last seen">
            {formatDateTime(cluster.last_seen_at)}
          </MetadataRow>
        </dl>
      </div>

      {/* Possible Google Places match — only when there's a low-confidence
          candidate the view chose not to use as canonical. Helps operators
          investigate (visit website, check address) before correcting. */}
      {placesCandidate && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <MagnifyingGlassIcon className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="text-sm font-semibold text-amber-900 uppercase tracking-wider">
                  Possible Google Places match
                </h2>
                <p className="text-xs text-amber-800 mt-0.5">
                  Match score{' '}
                  {placesCandidate.match_score != null
                    ? `${Math.round(placesCandidate.match_score * 100)}%`
                    : '—'}{' '}
                  — below the 60% threshold to use as canonical. Investigate before
                  correcting.
                </p>
              </div>
            </div>
            {!showCorrectionForm && (
              <button
                type="button"
                onClick={() => handleUsePlacesMatch(placesCandidate)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 min-h-[44px] flex-shrink-0"
              >
                Use this match
              </button>
            )}
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                Name
              </dt>
              <dd className="text-amber-950">{placesCandidate.name || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                Type
              </dt>
              <dd className="text-amber-950">
                {placesCandidate.primary_type || '—'}
              </dd>
            </div>
            <div className="md:col-span-2">
              <dt className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                Address
              </dt>
              <dd className="text-amber-950">
                {placesCandidate.formatted_address || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                Phone
              </dt>
              <dd className="text-amber-950">{placesCandidate.phone || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                Website
              </dt>
              <dd>
                {placesCandidate.website ? (
                  <a
                    href={placesCandidate.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-900 hover:text-amber-950 underline break-all"
                  >
                    {placesCandidate.website}
                  </a>
                ) : (
                  <span className="text-amber-950">—</span>
                )}
              </dd>
            </div>
            {placesCandidate.lat != null && placesCandidate.lng != null && (
              <div className="md:col-span-2">
                <dt className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                  Coordinates
                </dt>
                <dd className="text-amber-950">
                  {placesCandidate.lat.toFixed(4)}, {placesCandidate.lng.toFixed(4)}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* LLM reasoning */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          LLM reasoning
        </h2>
        {cluster.llm_should_split && (
          <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg px-3 py-2 text-sm">
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              LLM suggests this cluster may need to be split into multiple
              distinct churches.
            </span>
          </div>
        )}
        <p className="text-sm text-gray-700 whitespace-pre-wrap">
          {cluster.llm_reasoning || 'No reasoning recorded.'}
        </p>
      </div>

      {/* Location map */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Location preview
        </h2>
        <ClusterLocationEmbed cluster={cluster} />
      </div>

      {/* Sources */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Sources ({sources.length})
        </h2>
        {sources.length === 0 ? (
          <p className="text-sm text-gray-500">No sources recorded.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {sources.map((s, idx) => (
              <li
                key={`${s.source_id || idx}`}
                className="py-2 flex items-start justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{s.raw_name || '(empty)'}</span>
                    <span className="text-gray-400 text-xs ml-2">
                      {s.source_type}
                    </span>
                  </p>
                  {(s.user_name || s.user_email) && (
                    <p className="text-xs text-gray-500">
                      {s.user_name || s.user_email}
                    </p>
                  )}
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {formatDateTime(s.contributed_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Corrections history */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Corrections history ({corrections.length})
        </h2>
        {corrections.length === 0 ? (
          <p className="text-sm text-gray-500">No corrections yet.</p>
        ) : (
          <ul className="space-y-3">
            {corrections.map((c) => {
              const changed = describeBeforeAfter(c.before, c.after);
              return (
                <li
                  key={c.id}
                  className="border border-gray-100 rounded-lg p-3 space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {formatDateTime(c.corrected_at)}
                    </span>
                  </div>
                  {c.note && (
                    <p className="text-sm text-gray-700 italic">"{c.note}"</p>
                  )}
                  {changed && changed.length > 0 && (
                    <ul className="text-xs text-gray-500 space-y-0.5">
                      {changed.map(({ key, before, after }) => (
                        <li key={key}>
                          <code className="text-gray-400">{key}:</code>{' '}
                          <span className="line-through text-gray-400">
                            {JSON.stringify(before)}
                          </span>{' '}
                          →{' '}
                          <span className="text-gray-700">
                            {JSON.stringify(after)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
